import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, Tag, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ServiceRequestForm from '../components/ServiceRequestForm';
import ScrollToTop from '../components/ScrollToTop';
import { content } from '../data/content';
import { globalStyles } from '../styles/globalStyles';
import { useLanguage } from '../context/LanguageContext';
import Contact from '../components/Contact';
import ModernShareSection from '../components/MinimalShare';
import CompactShareSection from '../components/MinimalShare';
import MinimalShare from '../components/MinimalShare';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
      ? 'http://localhost' 
      : window.location.origin);

  // Fetch article by slug
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);

        const langParam = language === 'mr' ? 'Marathi' : 'English';
        
        const response = await fetch(
          `${API_BASE_URL}/admin/api/get-blog.php?slug=${encodeURIComponent(slug)}&lang=${langParam}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch article');
        }

        const result = await response.json();
        
        console.log('Article Response:', result);

        if (!result.success || !result.data) {
          setError('Article not found');
          setArticle(null);
          return;
        }

        const articleData = result.data;

        const dateObj = articleData.created_at ? new Date(articleData.created_at) : new Date();
        const formattedDate = dateObj.toLocaleDateString(
          language === 'mr' ? 'mr-IN' : 'en-US',
          { year: 'numeric', month: 'long', day: 'numeric' }
        );

        const wordCount = articleData.content ? articleData.content.split(/\s+/).length : 0;
        const readTime = Math.max(1, Math.ceil(wordCount / 200));

        const transformedArticle = {
          id: articleData.id,
          title: articleData.title || 'Untitled',
          excerpt: articleData.excerpt || articleData.content?.substring(0, 200) || '',
          content: articleData.content || '<p>No content available</p>',
          category: articleData.category || 'General',
          date: formattedDate,
          readTime: readTime.toString(),
          image: articleData.image_url || articleData.image || '📄',
          slug: articleData.slug || slug,
          author: articleData.author || 'Shree Digital Solutions And Services',
          language: articleData.language || (language === 'mr' ? 'Marathi' : 'English')
        };

        setArticle(transformedArticle);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, language, API_BASE_URL]);

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = article?.title || '';

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${text} ${url}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
      default:
        break;
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... - Shree Digital Solutions And Services</title>
        </Helmet>
        <style>{globalStyles}</style>
        <Header content={content} />
        <div style={{ textAlign: 'center', padding: '100px 20px', minHeight: '60vh' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <p style={{ fontSize: '20px', color: '#666' }}>
            {language === 'mr' ? 'लोड होत आहे...' : 'Loading...'}
          </p>
        </div>
        <Footer language={language} />
      </>
    );
  }

  // Error or not found state
  if (error || !article) {
    return (
      <>
        <Helmet>
          <title>Article Not Found - Shree Digital Solutions And Services</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <style>{globalStyles}</style>
        <Header content={content} />
        <div style={{ textAlign: 'center', padding: '100px 20px', minHeight: '60vh' }}>
          <h1 style={{ fontSize: '48px', marginBottom: '20px' }}>404</h1>
          <p style={{ fontSize: '20px', marginBottom: '30px', color: '#666' }}>
            {language === 'mr' ? 'लेख सापडला नाही' : 'Article not found'}
          </p>
          <button
            onClick={() => navigate('/blogs')}
            style={{
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '50px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {language === 'mr' ? 'ब्लॉग्सवर परत जा' : 'Back to Blogs'}
          </button>
        </div>
        <Footer language={language} />
      </>
    );
  }

  // Clean excerpt for meta description (remove HTML tags)
  const cleanExcerpt = article.excerpt.replace(/<[^>]*>/g, '').substring(0, 160);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{article.title} - Shree Digital Solutions And Services</title>
        <meta name="description" content={cleanExcerpt} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={cleanExcerpt} />
        <meta property="og:url" content={`https://shreeservz.com/blogs/${article.slug}`} />
        <meta property="og:site_name" content="Shree Digital Solutions And Services" />
        {article.image && article.image.startsWith('http') && (
          <meta property="og:image" content={article.image} />
        )}
        <meta property="article:author" content={article.author} />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:section" content={article.category} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={cleanExcerpt} />
        {article.image && article.image.startsWith('http') && (
          <meta name="twitter:image" content={article.image} />
        )}
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://shreeservz.com/blogs/${article.slug}`} />
        
        {/* Language */}
        <meta property="og:locale" content={language === 'mr' ? 'mr_IN' : 'en_US'} />
      </Helmet>

      <style>{`
        ${globalStyles}
        
        .blog-post {
          padding: 60px 0;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .blog-post-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #ff6b35;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 30px;
          padding: 10px 20px;
          background: white;
          border-radius: 50px;
          border: 2px solid #ff6b35;
          transition: all 0.3s;
        }

        .back-button:hover {
          background: #ff6b35;
          color: white;
        }

        .blog-post-header {
          background: white;
          padding: 40px;
          border-radius: 16px;
          margin-bottom: 30px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .blog-post-image {
          width: 100%;
          background: linear-gradient(135deg, #fff5f2 0%, #ffe5dc 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 60px;
          margin-bottom: 30px;
          overflow: hidden;
        }

        .blog-post-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .blog-post-meta {
          display: flex;
          align-items: center;
          gap: 24px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #666;
          font-size: 14px;
        }

        .blog-post-category {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          background: #fff5f2;
          color: #ff6b35;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
        }

        .blog-post-title {
          font-size: 36px;
          color: #1a1a1a;
          margin-bottom: 20px;
          line-height: 1.3;
          font-weight: 700;
        }

        .blog-post-content {
          background: white;
          padding: 40px;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          margin-bottom: 30px;
        }

        .blog-post-content h2 {
          font-size: 28px;
          color: #1a1a1a;
          margin-top: 40px;
          margin-bottom: 20px;
          font-weight: 600;
        }

        .blog-post-content h2:first-child {
          margin-top: 0;
        }

        .blog-post-content h3 {
          font-size: 24px;
          color: #1a1a1a;
          margin-top: 30px;
          margin-bottom: 15px;
          font-weight: 600;
        }

        .blog-post-content p {
          font-size: 18px;
          line-height: 1.8;
          color: #333;
          margin-bottom: 20px;
        }

        .blog-post-content ul,
        .blog-post-content ol {
          margin-bottom: 20px;
          padding-left: 30px;
        }

        .blog-post-content li {
          font-size: 18px;
          line-height: 1.8;
          color: #333;
          margin-bottom: 10px;
        }

        .blog-post-content strong {
          color: #ff6b35;
          font-weight: 600;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        .blog-post-content a {
          color: #ff6b35;
          text-decoration: underline;
        }

        .blog-post-content a:hover {
          color: #ff8c42;
        }

        .blog-post-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 20px 0;
        }

        .blog-post-content blockquote {
          border-left: 4px solid #ff6b35;
          padding-left: 20px;
          margin: 20px 0;
          font-style: italic;
          color: #666;
        }

        .share-section {
          background: white;
          padding: 30px;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          text-align: center;
        }

        .share-title {
          font-size: 20px;
          color: #1a1a1a;
          margin-bottom: 20px;
          font-weight: 600;
        }

        .share-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .share-btn {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 2px solid #e0e0e0;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
        }

        .share-btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
        }

        .share-btn.facebook:hover {
          background: #1877f2;
          border-color: #1877f2;
          color: white;
        }

        .share-btn.twitter:hover {
          background: #1da1f2;
          border-color: #1da1f2;
          color: white;
        }

        .share-btn.linkedin:hover {
          background: #0077b5;
          border-color: #0077b5;
          color: white;
        }

        .share-btn.whatsapp:hover {
          background: #25D366;
          border-color: #25D366;
          color: white;
        }

        .share-btn.copy:hover {
          background: #ff6b35;
          border-color: #ff6b35;
          color: white;
        }

        .share-btn.copied {
          background: #25D366;
          border-color: #25D366;
          color: white;
        }

        @media (max-width: 768px) {
          .blog-post-content,
          .share-section {
            padding: 24px;
          }
          .blog-post-header{
             padding:0px;
          }
          .blog-post-meta{
              padding: 0px 24px;}

          .blog-post-title {
            font-size: 28px;
            padding: 0px 24px 24px 24px;
          }

          .blog-post-content h2 {
            font-size: 24px;
          }

          .blog-post-content h3 {
            font-size: 20px;
          }

          .blog-post-content p,
          .blog-post-content li {
            font-size: 16px;
          }
        }
      `}</style>

      <Header content={content} />

      <div className="blog-post">
        <div className="blog-post-container">
          <button className="back-button" onClick={() => navigate('/blogs')}>
            <ArrowLeft size={20} />
            {language === 'mr' ? 'ब्लॉग्सवर परत जा' : 'Back to Blogs'}
          </button>

          <div className="blog-post-header">
            <div className="blog-post-image">
              {article.image.startsWith('http') ? (
                <img src={article.image} alt={article.title} />
              ) : (
                article.image
              )}
            </div>
            
            <div className="blog-post-meta">
              <span className="blog-post-category">
                <Tag size={16} />
                {article.category}
              </span>
              <span className="meta-item">
                <Calendar size={16} />
                {article.date}
              </span>
              <span className="meta-item">
                <Clock size={16} />
                {article.readTime} {language === 'mr' ? 'मिनिट वाचन' : 'min read'}
              </span>
            </div>

            <h1 className="blog-post-title">{article.title}</h1>
          </div>

          <div 
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* <div className="share-section">
            <h3 className="share-title">
              {language === 'mr' ? 'हा लेख शेअर करा' : 'Share this article'}
            </h3>
            <div className="share-buttons">
              <button className="share-btn facebook" onClick={() => handleShare('facebook')}>
                <Facebook size={24} />
              </button>
              <button className="share-btn twitter" onClick={() => handleShare('twitter')}>
                <Twitter size={24} />
              </button>
              <button className="share-btn linkedin" onClick={() => handleShare('linkedin')}>
                <Linkedin size={24} />
              </button>
              <button className="share-btn whatsapp" onClick={() => handleShare('whatsapp')}>
                <Share2 size={24} />
              </button>
              <button 
                className={`share-btn copy ${copied ? 'copied' : ''}`}
                onClick={() => handleShare('copy')}
              >
                {copied ? <Check size={24} /> : <Copy size={24} />}
              </button>
            </div>
          </div> */}
  
        <MinimalShare article={article} language={language} />
        </div>

        
      </div>
      <Contact language={language} content={content} />       
      <Footer language={language} />
      <WhatsAppButton phoneNumber="919321415838" message="Hello, I need help with a service" />
      <ServiceRequestForm language={language} content={content} />
      <ScrollToTop />
    </>
  );
};

export default BlogPostPage;